// @flow @jsx h

import { Component, h } from 'preact'
import { ViewportIntersections } from '@nrk/dh-feature-components'
import { getScrollTop, bem } from '@nrk/dh-utils'
import { Map } from '../../../shared/Map/index'
import { DefaultCard } from './cards/DefaultCard/index'
import SweetScroll from 'sweet-scroll'

// Import card types
import type { SectionType } from '../../../domain/index'
import type { Rect } from './types'
import type { Label, Overlay } from '../../../shared/Map/index'

// Import styles
import style from './index.css'
import '@nrk/dh-feature-components/dist/module/Slideshow/index.css'

export type Props = {
  sections: SectionType[],
  overlays: Overlay[],
  labels: Label[],
  load: boolean,
  initialCard: number,
  sendGaEvent?: any => void
}

export type State = {
  displayLeft: boolean,
  displayRight: boolean,
  rect: Rect | null,
  visibleIndex: number
}

class MapStory extends Component<Props, State> {
  elm: HTMLDivElement
  sweetScroll: SweetScroll

  constructor (props: Props) {
    super(props)
    this.state = {
      displayLeft: true,
      displayRight: true,
      rect: null,
      visibleIndex: props.sections.length ? 0 : -1
    }
  }

  componentDidMount () {
    if (!('scrollBehavior' in this.elm.style && this.elm.scrollTo)) {
      this.sweetScroll = new SweetScroll({ horizontal: true }, this.elm)
    }
    this.handleResize()
    window.addEventListener('resize', this.handleResize)

    this.hideScrollbar()
    setTimeout(() => this.scrollTo(this.elm.children[this.props.initialCard], false), 300)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResize)
  }

  hideScrollbar () {
    const barHeight = this.elm.offsetHeight - this.elm.clientHeight
    this.elm.style.marginBottom = `-${barHeight}px`
    this.elm.style.maxHeight = `calc(100% + ${barHeight}px)`
  }

  handleResize = () => {
    const scrollTop = getScrollTop()
    const rect = this.elm.getBoundingClientRect()

    this.setState({
      rect: {
        width: rect.width,
        height: rect.height,
        top: rect.top + scrollTop,
        bottom: rect.bottom + scrollTop
      }
    })
  }

  scrollTo (card: HTMLElement, smooth: boolean = false) {
    const elementCenter = card.offsetLeft + card.offsetWidth / 2
    const containerWidth = this.elm.offsetWidth
    const left = elementCenter - containerWidth / 2
    if (this.sweetScroll) {
      this.sweetScroll.to({ left })
    } else {
      this.elm.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' })
    }
  }

  findActiveImage (): any {
    const containerWidth = this.elm.offsetWidth
    const scrollLeft = this.elm.scrollLeft
    return [...this.elm.children].find(card => {
      const leftOfCenter = card.offsetLeft < scrollLeft + containerWidth / 2
      const rightOfCenter = card.offsetLeft + card.offsetWidth > scrollLeft + containerWidth / 2
      return leftOfCenter && rightOfCenter
    })
  }

  setDisplayLeft (displayLeft: boolean) {
    if (displayLeft !== this.state.displayLeft) {
      this.setState({ displayLeft })
    }
  }

  setDisplayRight (displayRight: boolean) {
    if (displayRight !== this.state.displayRight) {
      this.setState({ displayRight })
    }
  }

  handleScrollLeft = () => {
    const activeCard = this.findActiveImage()
    if (activeCard.previousSibling) {
      this.scrollTo(activeCard.previousSibling, true)
    }
  }

  handleScrollRight = () => {
    const activeCard = this.findActiveImage()
    if (activeCard.nextSibling) {
      this.scrollTo(activeCard.nextSibling, true)
    }
  }

  handleCardChange = (intersections: any[]) => {
    const cardsSorted = intersections.filter(card => card.isIntersecting)
      .sort((a, b) => b.ratio - a.ratio)

    if (cardsSorted && cardsSorted.length > 0) {
      const card = cardsSorted[0]
      const visibleIndex = intersections.indexOf(card) + 1
      if (visibleIndex !== this.state.visibleIndex) {
        this.setState({ visibleIndex })
        const key = visibleIndex
        const section = this.props.sections[this.state.visibleIndex]
        this.props.sendGaEvent &&
        this.props.sendGaEvent({
          action: 'SHOW_CONTENT',
          label: `${key}/${section.type}(${section.title})`
        })
      }
    }

    const leftCard = intersections[0]
    const rightCard = intersections[intersections.length - 1]
    this.setDisplayLeft(leftCard.ratio < 1)
    this.setDisplayRight(rightCard.ratio < 1)
  }

  render () {
    const { sections, overlays, labels } = this.props
    const { visibleIndex, rect, displayLeft, displayRight } = this.state
    let width = null
    if (rect) {
      width = rect.width
    }

    return (
      <div className={style.mapStory}>
        <div class={bem(style.mapStory__mapContainer)}>
          <div class={style.mapStory__map}>
            <Map section={sections[visibleIndex]} overlays={overlays} labels={labels} />
          </div>
        </div>
        <ViewportIntersections
          className={style.mapStory__cards}
          onChange={this.handleCardChange}
          threshold={[0, 0.5, 1]}
          ref={intersectionsElm => (this.elm = intersectionsElm.elm)}>
          {
            sections
              .filter(section => section.type === 'normal')
              .map((section: any, idx) => {
                return <DefaultCard
                  width={width}
                  key={String(idx)}
                  title={section.title}
                  content={section.text}
                  date={section.date}
                  alt={section.caption || section.alternativeText}
                />
              })
          }
        </ViewportIntersections>
        <button
          className={style.mapStory__left}
          onClick={this.handleScrollLeft}
          style={{ opacity: displayLeft ? 1 : 0 }}
          disabled={!displayLeft}
          aria-label='Rull til venstre'>
          <svg aria-hidden>
            <use xlinkHref='#nrk-chevron-left' />
          </svg>
        </button>
        <button
          className={style.mapStory__right}
          onClick={this.handleScrollRight}
          style={{ opacity: displayRight ? 1 : 0 }}
          disabled={!displayRight}
          aria-label='Rull til høyre'>
          <svg aria-hidden>
            <use xlinkHref='#nrk-chevron-right' />
          </svg>
        </button>
      </div>
    )
  }
}

export default MapStory
